<template>
  <div class="yhc-ef-kazai-detail-input">
    <div v-if="initialized">
      <!-- 家財種別s の 表示と選択 (1つ以上ある場合のみ) -->
      <YhcEFKazaiDetailInputSelectKazaiType
        v-if="shouldShowKazaiTypeSelect"
        :currentKazaiId="kazaiData.kazaiTypeId"
        :kazaiTypes="selectableKazaiTypes"
        :userInputName="kazaiData.userInputName"
        :receptionDisabledKazaiTypes="receptionDisabledKazaiTypes"
        @selectKazaiType="(_kazaiTypeId) => (kazaiData.kazaiTypeId = _kazaiTypeId)"
        @updateUserInputName="(_name) => (kazaiData.userInputName = _name)"
      />
      <!-- 荷受け不可 家財種別s の 表示 (注意文言表示のみ ということ) -->
      <!--      <YhcEFKazaiDetailReceptionDisabledNotice-->
      <!--        :receptionDisabledKazaiTypes="receptionDisabledKazaiTypes"-->
      <!--        v-if="receptionDisabledKazaiTypes.length > 0"-->
      <!--      />-->
      <!-- サイズ指定の入力 -->
      <YhcEFKazaiDetailInputSetSizes
        :kazaiData="kazaiData"
        @updateSizes="(sizes) => updateSizes(sizes)"
      />
      <!-- 家財毎 オプション選択の表示 -->
      <YhcEFKazaiDetailInputSelectKazaiOptions
        :kazaiType="selectedKazaiType"
        v-if="selectedKazaiType?.serviceOptions?.length"
        :selectedOptionsByServiceId="kazaiData.selectedOptionsByServiceId"
        :originService="originServiceNames.kazai"
        @selectOptions="
          (_selectedOptionsByServiceId) =>
            (kazaiData.selectedOptionsByServiceId = _selectedOptionsByServiceId)
        "
      />

      <!-- ご注意事項文言の表示 -->
      <YhcEFKazaiDetailInputNoticeByKazaiType
        :kazaiType="selectedKazaiType"
        v-if="selectedKazaiType.notice_text"
      />
      <!--      <pre>kazaiData.selectedOptionsByServiceId: {{kazaiData.selectedOptionsByServiceId}}</pre>-->
      <!-- Submit button の表示 -->
      <div class="yhc-ef-kazai-detail-input--submit-area text-center mt-3">
        <button
          :class="isSubmittable ? 'btn-yhc' : 'btn btn-secondary disabled text-white'"
          :disabled="!isSubmittable"
          @click.prevent="() => addNewKazai()"
        >
          <ficon :type="existingKazaiData ? 'edit' : 'plus'" />
          この内容で{{ existingKazaiData ? '更新' : '追加' }}する
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { YhcEstimateFormFrontService } from '../../YhcEstimateFormFrontService'
import {
  ModelKazaiOptionServicesOneSelection,
  ModelYhcKazaiTypesFullRelation,
  ModelYhcKazaiTypesSubCategories,
} from '../../../../fireModels/yhcDataTypes'
import YhcEFKazaiDetailInputSelectKazaiType from './YhcEFKazaiDetailInputSelectKazaiType.vue'
import YhcEFKazaiDetailInputSetSizes from './YhcEFKazaiDetailInputSetSizes.vue'
import YhcEFKazaiDetailInputNoticeByKazaiType from './YhcEFKazaiDetailInputNoticeByKazaiType.vue'
import YhcEFKazaiDetailInputSelectKazaiOptions from '../YhcEFItemList/YhcEFKazaiDetailInputSelectKazaiOptionsList.vue'
import {
  YhcEFInputOneKazaiTypeDataObject,
  YhcEFInputSelectedOptionsByServiceId,
} from '../../YhcEFInputOneKazaiTypeDataObject'
import { PropType, reactive, ref, computed, onMounted, inject } from 'vue'
import { originServiceNames } from '../../formUtils'

/**
 * 家財の Details を 入力するコンポーネント
 *
 * ## 責任範囲
 *
 * ### kazaiType 選択
 * - kazaiTypes選択: 渡された subCategoryId に対応する家財の kazaiTypes を 択一で選択させる
 *   - types が 1種類の場合には 自動選択にて 選択肢は表示させない
 * - 選択された kazaiType: 注意書き (以下の家財については荷受けできません。) を表示
 *
 * ### お荷物のサイズ (荷受け不可の場合は表示しない)
 * - 高さ・幅・奥行 を入力させる
 *
 * ### オプションの表示と選択 (荷受け不可の場合は表示しない)
 * - 選択された kazaiType の オプションが有るならば 複数表示する
 * - オプションごとに 選択肢を表示する
 *
 * ### ご注意事項の表示 (荷受け不可の場合は表示しない)
 * - 選択された kazaiType に対応する 注意事項を表示する
 *
 * ### "+ この内容で追加する" ボタン (荷受け不可の場合は表示しない)
 * - 入力された内容を追加する ($emit)
 *
 */
export default {
  name: 'YhcEFKazaiDetailInput',
  components: {
    YhcEFKazaiDetailInputSelectKazaiOptions,
    YhcEFKazaiDetailInputNoticeByKazaiType,
    YhcEFKazaiDetailInputSetSizes,
    YhcEFKazaiDetailInputSelectKazaiType,
  },
  props: {
    subCategoryId: {
      type: Number,
      required: true,
    },
    existingKazaiData: {
      type: Object as PropType<YhcEFInputOneKazaiTypeDataObject>,
      default: null,
    },
  },
  emits: ['addKazai'],
  setup(props, { emit }) {
    const YhcEstimateFormFrontServiceInstance = inject(
      'YhcEstimateFormFrontServiceInstance',
    ) as YhcEstimateFormFrontService
    const initialized = ref(false)
    const kazaiData = ref(
      props.existingKazaiData?.clone() ||
        reactive(
          new YhcEFInputOneKazaiTypeDataObject(YhcEstimateFormFrontServiceInstance),
        ),
    )

    const relatedKazaiTypes = computed(() => {
      return YhcEstimateFormFrontServiceInstance.kazaiTypesWithBaseDatetime.filter(
        (kazaiType) => {
          return kazaiType.subCategory?.id === props.subCategoryId
        },
      )
    })

    const selectableKazaiTypes = computed(() => {
      return relatedKazaiTypes.value.filter((kazaiType) => {
        return kazaiType.receptionDisabled !== true
      })
    })

    const receptionDisabledKazaiTypes = computed(() => {
      return relatedKazaiTypes.value.filter(
        (kazaiType) => kazaiType.receptionDisabled === true,
      )
    })

    const selectedKazaiType = computed(() => {
      return (
        selectableKazaiTypes.value.find(
          (kazaiType) => kazaiType.id === kazaiData.value.kazaiTypeId,
        ) || null
      )
    })

    const isSubmittable = computed(() => {
      return kazaiData.value.isAddable
    })

    const shouldShowKazaiTypeSelect = computed(() => {
      return true // 03.13 一旦 常に表示でも良いかも...
    })

    onMounted(() => {
      if (!kazaiData.value.kazaiTypeId) {
        kazaiData.value.kazaiTypeId = selectableKazaiTypes.value[0]?.id || null
      }
      initialized.value = true
    })

    const addNewKazai = () => {
      if (!kazaiData.value.isAddable) {
        return
      }
      if (props.existingKazaiData) {
        kazaiData.value.id = props.existingKazaiData.id
      }
      emit('addKazai', kazaiData.value)
    }

    const updateSizes = (sizes) => {
      kazaiData.value.size_depth = sizes.size_depth
      kazaiData.value.size_height = sizes.size_height
      kazaiData.value.size_width = sizes.size_width
    }

    return {
      initialized,
      kazaiData,
      relatedKazaiTypes,
      selectableKazaiTypes,
      receptionDisabledKazaiTypes,
      selectedKazaiType,
      isSubmittable,
      shouldShowKazaiTypeSelect,
      addNewKazai,
      updateSizes,
      originServiceNames,
    }
  },
}
</script>
